import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { combineLatest } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { UnsubscribeComponent } from '../../generals/Components';
import { convertToTreeNodes, getSelectedTreeNodes, treePartialNodes } from '../../helpers/helper';
import { Category, ProductAttributeEditor } from '../../interfaces/interfaces';

@Component({
  selector: 'app-productattributeeditor',
  templateUrl: './productattributeeditor.component.html',
  styleUrls: ['./productattributeeditor.component.scss']
})
export class ProductattributeeditorComponent extends UnsubscribeComponent implements AfterViewInit {
  
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  formats: { label: string, value: string }[] = [
    { label: 'Szöveges', value: 'TEXT' },
    { label: 'Szám', value: 'NUMBER' },
    { label: 'Igaz / Hamis', value: 'CHECKBOX' }
  ];

  productAttributeId: number = null;
  dataLoading = false;
  loading = false;
  productAttributeEditorFormGroup: FormGroup = null;

  categoriesTreeNodes: TreeNode<Category>[] = [];
  selectedCategoriesTreeNodes: TreeNode<Category>[] = [];

  set categories(categories: Category[]) {
    this.categoriesTreeNodes = convertToTreeNodes<Category>(categories);
  }

  set selectedCategoryIds(ids: number[]) {
    this.selectedCategoriesTreeNodes = getSelectedTreeNodes<Category>(this.categoriesTreeNodes, ids);
  }

  init(): void {
    this.initProductAttributeEditorFormGroup();
    this.loadData();
  }

  initProductAttributeEditorFormGroup(): void {
    this.productAttributeEditorFormGroup = new FormGroup({
      name: new FormControl('', [Validators.required]),
      format: new FormControl('TEXT', [Validators.required]),
      suffix: new FormControl(null),
      hidden: new FormControl(false)
    });
    const formatFormControl: FormControl = this.productAttributeEditorFormGroup.get('format') as FormControl;
    const suffixFormControl: FormControl = this.productAttributeEditorFormGroup.get('suffix') as FormControl;
    formatFormControl.valueChanges.pipe(startWith(formatFormControl.value)).subscribe((format: string) => {
      if (format === 'NUMBER') {
        suffixFormControl.setValidators([Validators.required]);
      } else {
        suffixFormControl.setValue(null);
        suffixFormControl.clearValidators();
      }
      suffixFormControl.updateValueAndValidity({
        emitEvent: false
      });
    })
  }

  loadData(): void {
    this.dataLoading = true;
    if (this.productAttributeId !== null) {
      combineLatest([
        this.apiService.getProductAttribute(this.productAttributeId),
        this.apiService.getCategories()
      ]).pipe(takeUntil(this.unsubscribe)).subscribe(([{ attributeEditor }, { categories }]) => {
        this.productAttributeEditorFormGroup.get('name').setValue(attributeEditor.names[0].name);
        this.productAttributeEditorFormGroup.get('suffix').setValue(attributeEditor.suffix);
        this.productAttributeEditorFormGroup.get('format').setValue(attributeEditor.format);
        this.productAttributeEditorFormGroup.get('hidden').setValue(attributeEditor.hidden);
        this.categories = categories;
        this.selectedCategoryIds = attributeEditor.categories;
        treePartialNodes(this.categoriesTreeNodes, this.selectedCategoriesTreeNodes);
      }).add(() => {
        this.dataLoading = false;
      });
    } else {
      this.apiService.getCategories().pipe(takeUntil(this.unsubscribe)).subscribe((rsp) => {
        this.categories = rsp.categories;
      }).add(() => {
        this.dataLoading = false;
      });
    }
  }

  save(): void {
    if (!this.productAttributeEditorFormGroup.valid) {
      return;
    }
    this.loading = true;
    const productAttributeEditorFormValue: ProductAttributeEditor = {
      categories: this.selectedCategoriesTreeNodes.filter(node => node.children.length === 0).map((node) => node.data.id),
      format: this.productAttributeEditorFormGroup.get('format').value,
      suffix: this.productAttributeEditorFormGroup.get('suffix').value,
      names: [{ name: this.productAttributeEditorFormGroup.get('name').value, languageId: 'hu' }],
      hidden: this.productAttributeEditorFormGroup.get('hidden').value
    };
    if (this.productAttributeId === null) {
      this.apiService.createProductAttribute(productAttributeEditorFormValue).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.ref.close(true);
      }).add(() => {
        this.loading = false;
      });
    } else {
      this.apiService.updateProductAttribute(this.productAttributeId, productAttributeEditorFormValue).pipe(takeUntil(this.unsubscribe)).subscribe(() => {
        this.ref.close(true);
      }).add(() => {
        this.loading = false;
      });
    }
  }

  close(result: boolean = false): void {
    this.ref.close(result);
  }

  ngAfterViewInit(): void {
    this.input.nativeElement.focus();
  }

  constructor(
    private apiService: ApiService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) {
    super();
    this.productAttributeId = this.config.data.productAttributeId;
    this.init();
  }
}
